<template>
  <div>
    <ExitNavBar :title="getNavTitle" v-on:exit="exit()" />
    <OrderEditTimeoutInfo
      v-if="showTimeoutInfoForOrderEdit"
      @timeout-reached="onTimeoutReached()"
      :order-created-at="getOrderCreatedDate() as Date" />
    <ButtonDisabledInfoStripe
      v-if="warningMessage"
      :warning-message="warningMessage"
      :account-receivables-email="accountReceivablesEmail" />
    <div class="add-edit-order">
      <!-- id is used to calculate sticky position of multiple sticky elements -->
      <div class="navbar-background" id="addEditNavBar">
        <div class="add-edit-order-nav-bar" data-cy="orders-nav-bar">
          <div class="requested-date" data-cy="requested-date">
            <div v-if="showRequestedDateSelector">
              <BackstageInputCalendar
                v-model="selectedDate"
                name="requested-date"
                :label="$t('matrix.requested_date')"
                :disabled="areExistingOrders"
                :min-date="minimumDate"
                :max-date="maximumDate"
                :placeholder="$t(`matrix.ship_date_placeholder`)"
                v-on:update:modelValue="onOrderDateChange" />
            </div>
          </div>
          <BreadcrumbMenu
            v-if="showCurrentStepContent"
            :key="refreshBreadCrumbMenu"
            :crumbs="orderBreadcrumbMenuItems" />
          <div class="action-buttons">
            <BackstageButton
              :disabled="!currentStepActionIsEnabled"
              :icon-right="submitting ? 'loading' : 'arrow-right'"
              :data-cy="`${currentStep}-button-cnt`"
              :data-cy-button="`${currentStep}-button`"
              v-on:click="executeCurrentStepAction">
              {{ currentStepActionButtonLabel }}
            </BackstageButton>
          </div>
        </div>
      </div>
      <div
        v-if="showCurrentStepContent"
        class="current-step-content"
        :class="[{ 'pop-up-open': showSubmitReminderPopup }]">
        <ProductsModelsSelector
          v-if="currentOrderStepIsProductModelsSelection"
          :key="startRequestedDate"
          :order-type="orderType"
          :account-id="selectedAccountId"
          :order-date="startRequestedDate"
          :pre-selected-product-model-names="allSelectedProductModels"
          :order-number="orderNumber"
          v-on:selected-product-models="setSelectedProductModels" />

        <ProductsConfirmationModal
          v-if="showProductsConfirmationModal"
          :matrixOrders="matrixOrders"
          :matrixType="matrixType"
          @proceed="proceedToOrderDetails(true)">
          <BackstageButton @click="showProductsConfirmationModal = false" :variant="ButtonVariant.SECONDARY">
            <BackstageIcon name="arrow-left" />
            <span class="spacer"> {{ $t(`viewOrder.confirmation_modal.go_back`) }}</span>
          </BackstageButton>
          <BackstageButton @click="proceedToOrderDetails(false)" :variant="ButtonVariant.PRIMARY">
            <span>{{ $t(`viewOrder.confirmation_modal.proceed`) }}</span>
          </BackstageButton>
        </ProductsConfirmationModal>

        <ProductsQuantitiesSelector
          v-if="currentOrderStepIsQuantities"
          :matrix-type="matrixType"
          :account="account"
          :default-size="size"
          :selected-product-models="allSelectedProductModels"
          :matrix-orders="matrixOrders"
          :submitting="submitting"
          :minimum-date="minimumDate"
          :maximum-date="maximumDate"
          :multiple="multiple"
          :is-existing-order="areExistingOrders"
          :order-number="orderNumber"
          v-on:all-orders-are-valid="updateAllOrdersAreValid"
          v-on:matrix-touched="orderQuantitiesTouched = true"
          @update:matrixOrders="updateMatrixOrders"
          v-on:delete-draft="deleteDraft"
          :selectedDate="selectedDate"
          :selectedOrderTypeInfo="selectedOrderInitInfo" />

        <div v-if="currentOrderStepIsOrderDetails" class="common-container">
          <ViewEditOrders
            :key="startRequestedDate + 'order-details'"
            :order-items="orders"
            :order-is-draft="isDraft"
            :maximum-date="maximumDate"
            v-on:add-new-order="addNewOrder"
            v-on:orders-are-valid="setOrdersAreValidFromViewEditOrders"
            v-on:update-orders="updateOrdersFromViewEditOrders"
            v-on:edit-order-quantities="editOrderQuantities" />
        </div>
        <div v-if="showSubmitReminderPopup">
          <NotificationMessage
            :title="$t('viewOrder.submit_reminder_popup_title')"
            :size="'small'"
            :show-close-button="true"
            v-on:close="showSubmitReminderPopup = false">
            <SubmitReminder
              :submitting="submitting"
              :saving="saving"
              :deleting="deleting"
              :disabled-actions="!isDraft ? ['delete', 'save'] : []"
              :submit-enabled="isSubmitEnabled && isRequestedReceiptDateValid"
              :save-draft-enabled="allOrdersViewValid && isRequestedReceiptDateValid"
              v-on:save="updateDraftOnExit"
              v-on:delete="deleteDraftOnExit"
              v-on:submit="submitOrders" />
          </NotificationMessage>
        </div>
      </div>
      <div v-else class="loader"></div>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import BreadcrumbMenu from '@/components/common/breadcrumb-menu.vue';
import ButtonDisabledInfoStripe from '@/components/common/ButtonDisabledInfoStripe.vue';
import ExitNavBar from '@/components/common/exit-nav-bar.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import SubmitReminder from '@/components/common/submit-reminder.vue';
import ProductsConfirmationModal from '@/components/matrix/products-confirmation-modal.vue';
import ProductsModelsSelector from '@/components/matrix/products-models-selector.vue';
import ProductsQuantitiesSelector from '@/components/matrix/products-quantities-selector.vue';
import OrderEditTimeoutInfo from '@/components/orders/order-edit-timeout-info.vue';
import ViewEditOrders from '@/components/orders/view-edit-orders.vue';
import { useAnalytics } from '@/composables/analytics';
import { AccountsReceivablesEmails } from '@/constants/accounts-receivables.constants';
import { globalConstants } from '@/constants/global.constants';
import { orderOverviewId } from '@/constants/order.constants';
import { OrderEditType } from '@/constants/orderEditType';
import { accountMixins } from '@/mixins/account.mixins';
import { localeMixins } from '@/mixins/locale.mixins';
import { ordersMixins } from '@/mixins/orders.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { validationMixins } from '@/mixins/validation.mixins';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { type BreadcrumbItem } from '@/models/breadcrumb-item';
import { ButtonVariant } from '@/models/button-variant.enum';
import { GaCheckoutStepNames, GaCustomEvents } from '@/models/ga-custom-events.enum';
import { MatrixOrder } from '@/models/matrix-order.model';
import { MatrixProduct } from '@/models/matrix-product.model';
import { MatrixType } from '@/models/matrix-type.enum';
import { OrderAction } from '@/models/order-action.enum';
import { OrderProduct } from '@/models/order-product.model';
import { OrderTypeEnum } from '@/models/order-type.enum';
import { Order } from '@/models/order.model';
import { AccountsService } from '@/services/accounts.service';
import { DraftsService } from '@/services/drafts.service';
import { OrdersService } from '@/services/orders.service';
import {
  AccountDetailsResponseDto,
  DraftInitInfoDto,
  DraftModel,
  DraftProductModel,
  OrderLineUpdateDto,
  OrderStatus,
  OrderType,
  OrderTypeDto,
  OrderUpdateDto,
  ValidatorResult,
} from '@onrunning/backstage-models';
import { cloneDeep } from 'lodash';
import { storeToRefs } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { defineComponent, ref } from 'vue';
import { useRouter } from 'vue-router';
import BackstageButton from '../components/common/BackstageButton.vue';
import { useFormat } from '../composables/format';
import { useOrderValidation } from '../composables/useOrderValidation';
import { RouteNames } from '../router';
import { useAddEditOrderStore } from '../stores/add-edit-order';
import { useUserStore } from '../stores/user';
import { isDateAfter, subtractDateDuration, today, toISOString, toJsDate, toPlainDate } from '../utils/temporalUtils';

export default defineComponent({
  name: 'add-edit-order',
  components: {
    ProductsModelsSelector,
    ProductsQuantitiesSelector,
    ProductsConfirmationModal,
    ViewEditOrders,
    BreadcrumbMenu,
    ExitNavBar,
    SubmitReminder,
    NotificationMessage,
    BackstageButton,
    BackstageInputCalendar,
    BackstageIcon,
    OrderEditTimeoutInfo,
    ButtonDisabledInfoStripe,
  },
  props: {
    id: {
      type: String,
      default: undefined,
    },
    editType: {
      type: String,
      default: undefined,
    },
    contactId: {
      type: String,
      default: undefined,
    },
  },
  setup(props) {
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const { isSalesRep, userProfile, isDealer } = useUserStore();
    const { isPreOrderAndOutsideInitialSellIn } = useOrderValidation(props.editType as OrderEditType);
    const {
      setEditingMatrixOrders,
      setEditingOrders,
      setSelectedProductModels,
      setInitialCustomerPoNumber,
      selectedProductModels,
      getInitialCustomerPoNumber,
      $reset,
      editingMatrixOrders,
      editingOrders,
      initialCustomerPoNumber,
    } = useAddEditOrderStore();

    const { formatDateToIsoStringIgnoringTime, formatNonLocalisedDateString } = useFormat();
    const { addCustomGaEvent } = useAnalytics();

    const isTimeoutForOrderEditing = ref(false);
    const router = useRouter();

    return {
      account: selectedAccount,
      addCustomGaEvent,
      selectedAccountId,
      isSalesRep,
      isDealer,
      resetAddEditStore: $reset,
      setEditingMatrixOrders,
      setEditingOrders,
      setInitialCustomerPoNumber,
      setStoreSelectedProductModels: setSelectedProductModels,
      storesSelectedProductModels: selectedProductModels,
      getInitialCustomerPoNumber,
      editingMatrixOrders,
      editingOrders,
      userProfile,
      ButtonVariant,
      formatDateToIsoStringIgnoringTime,
      formatNonLocalisedDateString,
      isTimeoutForOrderEditing,
      router,
      initialCustomerPoNumber,
      isPreOrderAndOutsideInitialSellIn,
    };
  },
  data() {
    return {
      showCurrentStepContent: false,
      submitting: false,
      saving: false,
      deleting: false,
      canLeavePage: false,
      shouldLoadProductsQuantities: false,
      showSubmitReminderPopup: false,
      inOrderDetails: false,
      allOrdersQuantitiesAreValid: false,
      allOrdersViewValid: true,
      orders: [] as Order[] | DraftModel[],
      draftsService: new DraftsService(),
      ordersService: new OrdersService(),
      size: undefined as string | undefined,
      matrixOrders: new Array<MatrixOrder>(),
      draftInitInfo: undefined as DraftInitInfoDto | undefined,
      orderNumber: undefined as string | undefined,
      selectedProductModels: [] as string[],
      refreshBreadCrumbMenu: 0,
      orderOverviewId: orderOverviewId,
      multiplePreOrdersGroupId: undefined as undefined | string,
      showRelatedOrders: this.$router.currentRoute.value.query.showRelatedOrders === 'true',
      orderQuantitiesTouched: false,
      currentStep: AddEditOrderBreadcrumbSteps.productsModelsSelection,
      showProductsConfirmationModal: false,
      isCustomerPoNumberChanged: false,
      accountsService: new AccountsService(),
      accountReceivablesEmail: undefined as string | undefined,
      accountDetails: undefined as AccountDetailsResponseDto | undefined,
    };
  },
  computed: {
    canDealerEditOrder(): boolean {
      if (!this.isEditScreen || !this.isDealer() || this.orders.length === 0) {
        return false;
      }

      const firstOrder = this.orders[0] as Order;

      return this.isOrderEditableForDealer(firstOrder, this.userProfile?.user);
    },
    showTimeoutInfoForOrderEdit(): boolean {
      return this.canDealerEditOrder;
    },
    isDealersOrderEditingAllowed(): boolean {
      return this.canDealerEditOrder && !this.isTimeoutForOrderEditing;
    },
    isSubmitEnabled(): boolean {
      return (
        this.allOrdersViewValid &&
        this.hasValidContactId &&
        !this.submitting &&
        !this.accountDetails?.onHold &&
        (this.isEditScreen && !this.isDraft && this.isDealer() ? this.isDealersOrderEditingAllowed : true)
      );
    },
    isEditScreen(): boolean {
      return this.router.currentRoute.value.name === RouteNames.EditOrder;
    },
    warningMessage(): string | null {
      if (!this.isRequestedReceiptDateValid) {
        return this.$t('viewOrder.submit_button_disabled_rrd');
      }
      if (this.accountDetails?.onHold) {
        return this.$t('viewOrder.submit_button_disabled_acc_onhold');
      }

      return null;
    },
    getNavTitle(): string {
      if (!this.orderType) {
        return '';
      }

      const title = localeMixins.getLocaleTextByOrderType('viewOrders.order_type.', this.orderType);

      const navTitle = this.$t(title).toString();

      if (this.orderType === OrderType.MultiplePreOrder) {
        return navTitle;
      }

      const orderNumber = this.orders[0]?.orderNumber ? ` - ${this.orders[0].orderNumber}` : '';

      return navTitle + orderNumber;
    },
    orderBreadcrumbMenuItems(): BreadcrumbItem[] {
      const items = [
        this.getOrderBreadcrumbMenuItem(AddEditOrderBreadcrumbSteps.productsModelsSelection),
        this.getOrderBreadcrumbMenuItem(AddEditOrderBreadcrumbSteps.productsQuantities),
        this.getOrderBreadcrumbMenuItem(AddEditOrderBreadcrumbSteps.orderDetails),
      ];

      return items;
    },
    currentStepActionIsEnabled(): boolean {
      if (this.submitting) {
        return false;
      }

      // If we're in the edit mode and the dealer has no permission to edit the order, we should disable the action button.
      if (this.isDealer() && !this.isDraft && this.isEditScreen && !this.isDealersOrderEditingAllowed) {
        return false;
      }

      switch (this.currentStep) {
        case AddEditOrderBreadcrumbSteps.productsModelsSelection:
          return this.selectedProductModels.length > 0;
        case AddEditOrderBreadcrumbSteps.productsQuantities:
          return this.allOrdersQuantitiesAreValid;
        case AddEditOrderBreadcrumbSteps.orderDetails:
          return (
            this.allOrdersViewValid &&
            this.isRequestedReceiptDateValid &&
            this.hasValidContactId &&
            (!this.isDraft ? this.isOrderUpdateValid : true)
          );
        default:
          return true;
      }
    },
    hasValidContactId(): boolean {
      if (!this.isSalesRep()) {
        return true;
      }

      if (this.contactId) {
        return true;
      }

      return this.orders
        .filter((order) => order.orderNumber !== this.orderOverviewId)
        .every((order) => order.contactId);
    },
    isRequestedReceiptDateValid() {
      let isRequestedReceiptDateValid;

      if (!this.isPreOrderVariant(this.orderType)) {
        isRequestedReceiptDateValid = true;
      } else {
        isRequestedReceiptDateValid = !this.matrixOrders.some((matrixOrder) =>
          isDateAfter(matrixOrder.date ?? today(), this.maximumDate),
        );
      }

      return isRequestedReceiptDateValid;
    },
    allSelectedProductModels(): string[] {
      const orderProducts = (this.orders as Order[])?.reduce((productGroupNames: string[], order: Order) => {
        order.products?.map((product) => {
          if (product.productGroupName) {
            productGroupNames.push(product.productGroupName);
          }
        });

        return productGroupNames;
      }, []);

      const allSelectedProductModels = new Set<string>([...this.selectedProductModels, ...orderProducts]);

      return Array.from(allSelectedProductModels);
    },
    matrixType(): MatrixType {
      const orderType = this.orderType?.toLocaleLowerCase();
      if (orderType === OrderTypeEnum.LastSeason.toLocaleLowerCase()) {
        return MatrixType.lastSeason;
      }

      const reorderTypes = [OrderTypeEnum.ReOrder, OrderTypeEnum.DropShip].map((type) => type.toLocaleLowerCase());

      return reorderTypes.includes(orderType) ? MatrixType.order : MatrixType.preorder;
    },
    orderType(): OrderType {
      const quantityOrders = this.orders.length;

      return this.$route.params['orderType']
        ? this.mapOrderTypeByOrderType(this.$router.currentRoute.value.params['orderType'] as string)
        : this.orders[quantityOrders - 1]?.type;
    },
    multiple(): boolean {
      return this.orderType?.toLocaleLowerCase() === OrderTypeEnum.PreOrderMultiple.toLocaleLowerCase();
    },
    selectedDate: {
      get(): Date {
        return new Date(this.matrixOrders[0].date ?? toISOString(today()));
      },
      set(newDate: Date) {
        this.matrixOrders[0].date = newDate;
      },
    },
    minimumDate(): Date {
      if (!this.selectedOrderInitInfo) {
        return toJsDate(subtractDateDuration(today(), { days: 1 }));
      }

      return toJsDate(toPlainDate(this.selectedOrderInitInfo?.startDate));
    },
    maximumDate(): any {
      if (!this.selectedOrderInitInfo || !this.isPreOrderVariant(this.orderType)) {
        return new Date(globalConstants.maximumDate);
      }

      return this.selectedOrderInitInfo?.endDate
        ? toJsDate(toPlainDate(this.selectedOrderInitInfo.endDate))
        : new Date(globalConstants.maximumDate);
    },
    startRequestedDate(): string {
      return this.formatDateToIsoStringIgnoringTime(
        this.$router.currentRoute.value.params['startDate']
          ? (this.$router.currentRoute.value.params['startDate'] as string)
          : this.formatNonLocalisedDateString(new Date()),
      );
    },
    showRequestedDateSelector(): boolean {
      return (
        this.editType !== OrderEditType.Order &&
        this.matrixOrders.length > 0 &&
        this.orderType?.toLocaleLowerCase() !== OrderType.MultiplePreOrder.toLocaleLowerCase()
      );
    },
    areExistingOrders(): boolean {
      const quantityOrders = this.orders.length;

      if (this.id && quantityOrders) {
        // it shouldn't be the case some orders are drafts and some not
        return !this.theOrderIsDraft(this.orders[quantityOrders - 1]);
      }

      return false;
    },
    currentOrderStepIsProductModelsSelection(): boolean {
      const isStep = this.currentStep === AddEditOrderBreadcrumbSteps.productsModelsSelection;

      if (isStep) {
        this.addCustomGaEvent({
          event: GaCustomEvents.CheckoutStep2,
          checkout_step_name: GaCheckoutStepNames.SelectModels,
        });
      }

      return isStep;
    },
    currentOrderStepIsQuantities(): boolean {
      const isStep = this.currentStep === AddEditOrderBreadcrumbSteps.productsQuantities;

      if (isStep) {
        this.addCustomGaEvent({
          event: GaCustomEvents.CheckoutStep3,
          checkout_step_name: GaCheckoutStepNames.AddQuantities,
        });
      }

      return isStep;
    },
    currentStepActionButtonLabel(): string {
      switch (this.currentStep) {
        case AddEditOrderBreadcrumbSteps.productsModelsSelection:
          return this.$t(
            `create_edit_order.execute_${AddEditOrderBreadcrumbSteps.productsModelsSelection}_step_button`,
          );
        case AddEditOrderBreadcrumbSteps.productsQuantities:
          return this.$t(`create_edit_order.execute_${AddEditOrderBreadcrumbSteps.productsQuantities}_step_button`);
        case AddEditOrderBreadcrumbSteps.orderDetails:
          return this.accountDetails?.onHold
            ? this.$t(`create_edit_order.execute_save_draft_button`)
            : this.$t(`create_edit_order.execute_${AddEditOrderBreadcrumbSteps.orderDetails}_step_button`);
        default:
          return this.$t(
            `create_edit_order.execute_${AddEditOrderBreadcrumbSteps.productsModelsSelection}_step_button`,
          );
      }
    },
    currentOrderStepIsOrderDetails(): boolean {
      const isStep = this.currentStep === AddEditOrderBreadcrumbSteps.orderDetails;

      if (isStep) {
        this.addCustomGaEvent({
          event: GaCustomEvents.CheckoutStep4,
          checkout_step_name: GaCheckoutStepNames.Review,
        });
      }

      return isStep;
    },
    isDraft(): boolean {
      return this.orders.length === 0 || this.orders.some((order) => order.status === OrderStatus.NotSubmitted);
    },
    areMultiplePreOrderDrafts(): boolean {
      return this.isDraft && this.orders.some((order) => order.type === OrderType.MultiplePreOrder);
    },
    isOrderUpdatePage(): boolean {
      return this.$route.name === RouteNames.EditOrder;
    },
    isOrderUpdateValid(): boolean {
      return (
        this.orders[0] &&
        this.areExistingOrders &&
        (this.getUpdateOrderPayload().lines.length > 0 || this.isCustomerPoNumberChanged)
      );
    },

    selectedOrderInitInfo(): OrderTypeDto {
      const orderTypeInitInfo = (this.draftInitInfo as DraftInitInfoDto)?.orderTypes.find(
        (type) => type.type.toLocaleLowerCase() === this.orderType?.toLowerCase(),
      ) as OrderTypeDto;

      return orderTypeInitInfo;
    },
  },
  watch: {
    $route(to) {
      this.canLeavePage = false;

      if (this.currentStep === AddEditOrderBreadcrumbSteps.productsQuantities) {
        this.setEditingMatrixOrders(this.matrixOrders);
      }

      this.currentStep = to.params['step'] ?? AddEditOrderBreadcrumbSteps.productsModelsSelection;
    },
    account: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.showCurrentStepContent = false;
        this.resetAddEditStore();
        this.init();
      }
    },
    getProductModels: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.selectedProductModels = newValue;
        }
      },
    },
    orders: {
      deep: true,
      handler() {
        if (this.orders.length === 0) {
          return;
        }

        const currentCustomerPoNumber = this.orders[0].customerPoNumber;
        const initialCustomerPoNumber = this.getInitialCustomerPoNumber();

        if ((!currentCustomerPoNumber || currentCustomerPoNumber === '') && !initialCustomerPoNumber) {
          this.isCustomerPoNumberChanged = false;

          return;
        }

        this.isCustomerPoNumberChanged = currentCustomerPoNumber !== initialCustomerPoNumber;
      },
    },
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.onBeforeUnloadHandler);
  },
  async mounted() {
    await this.init();

    if (this.orders.length > 0 && this.orders[0].customerPoNumber && !this.getInitialCustomerPoNumber()) {
      this.setInitialCustomerPoNumber(this.orders[0].customerPoNumber);
    }
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnloadHandler);
  },

  methods: {
    ...utilsMixins,
    ...accountMixins,
    ...ordersMixins,
    ...validationMixins,

    confirmNavigatingAway(path: string) {
      // ToDo fix globally
      this.$snotify.error(this.$t('global.warning_unsaved_changes'), {
        buttons: [
          {
            text: `${this.$t('global.continue_button')}`,
            action: () => {
              this.$router.push(path);
            },
            className: ButtonVariant.SECONDARY,
          },
        ],
      });
    },
    exit() {
      if (this.orders.length > 0) {
        this.showSubmitReminderPopup = true;
      } else if (
        (!this.matrixOrders[0]?.orderId && this.matrixOrders[0]?.total !== 0) ||
        (this.matrixOrders[0]?.orderId && this.orderQuantitiesTouched)
      ) {
        this.canLeavePage = true;
        this.confirmNavigatingAway('/orders');
      } else {
        this.$router.push('/orders');
      }
    },

    onBeforeUnloadHandler(event: BeforeUnloadEvent) {
      if (this.currentStep === AddEditOrderBreadcrumbSteps.productsQuantities) {
        this.setEditingMatrixOrders(this.matrixOrders);
      } else if (this.currentStep === AddEditOrderBreadcrumbSteps.orderDetails) {
        this.setEditingOrders(this.orders);
      }

      event.preventDefault();
      event.returnValue = '';
    },

    async init() {
      this.orders = [];

      const [result] = await Promise.all([
        this.draftsService.getInitInfo(this.selectedAccountId),
        this.checkAccountStatus(),
      ]);

      this.draftInitInfo = result.model;

      const defaultSize = this.$t(`config.default_size_run['${this.account?.billingCountry}']`);

      this.selectedProductModels = this.storesSelectedProductModels;

      if (globalConstants.sizeTypes.includes(defaultSize)) {
        this.size = defaultSize;
      }

      await this.initOrders();

      if (this.id && !this.orders.length) {
        this.$router.push('/orders');
      }

      this.initMatrixOrders();
      this.setInitialStep();

      this.showCurrentStepContent = true;
    },
    async checkAccountStatus() {
      this.accountDetails = (await this.accountsService.getAccountDetails(this.selectedAccountId))?.model;

      if (!this.accountDetails) {
        return this.$snotify.error(this.$t('global.error'));
      }

      if (this.accountDetails.onHold) {
        this.accountReceivablesEmail =
          AccountsReceivablesEmails[this.accountDetails.legalEntity as keyof typeof AccountsReceivablesEmails];
      }
    },
    onTimeoutReached() {
      this.isTimeoutForOrderEditing = true;
      this.redirectUserToLastStep();
    },

    getOrderCreatedDate(): Date | undefined {
      if (this.orders.length > 0) {
        return new Date(this.orders[0].date);
      }

      return undefined;
    },

    redirectUserToLastStep() {
      this.router.push({
        name: RouteNames.EditOrder,
        params: {
          id: this.orders[0].orderNumber,
          editType: this.orders[0].type,
          step: AddEditOrderBreadcrumbSteps.orderDetails,
        },
        force: true,
      });
    },

    setSelectedProductModels(selectedProductModels: string[]) {
      this.selectedProductModels = selectedProductModels;
      this.setStoreSelectedProductModels(this.selectedProductModels);
    },

    setInitialStep() {
      const urlStep = this.$route.params['step'] as string;

      if (
        (urlStep === AddEditOrderBreadcrumbSteps.productsQuantities && this.allSelectedProductModels.length === 0) ||
        (urlStep === AddEditOrderBreadcrumbSteps.orderDetails && !this.orders.length) ||
        !this.isAddEditOrderBreadcrumbStep(urlStep)
      ) {
        return this.$router.replace({
          params: {
            ...this.$route.params,
            step: AddEditOrderBreadcrumbSteps.productsModelsSelection,
          },
        });
      }

      this.currentStep = urlStep;
    },

    async loadRelatedOrders() {
      const mainOrder = this.orders[0];
      const responses = await Promise.all(
        (mainOrder?.relatedOrders ?? [])
          .filter((relatedOrder) => relatedOrder.id !== mainOrder.id)
          .map((relatedOrder) => this.draftsService.get(relatedOrder.id, this.selectedAccountId)),
      );

      if (responses.every((response) => response.isValid())) {
        responses.forEach((draftValidation) => {
          if (draftValidation.model) {
            (this.orders as DraftModel[]).push(draftValidation?.model);
          }
        });
      }
    },

    async initOrders() {
      if (this.editingOrders.length) {
        this.orders = this.editingOrders;
      } else if (this.id && this.orders.length === 0) {
        if (!this.editType || this.editType === OrderEditType.Draft) {
          const draftValidation = await this.draftsService.get(this.id, this.account?.id, this.showRelatedOrders);

          if (draftValidation.model) {
            (this.orders as DraftModel[]).push(draftValidation.model);
          } else {
            return this.$router.replace('/orders');
          }

          const isMultipleOrder = this.orders[0].type === OrderType.MultiplePreOrder;
          const hasRelatedOrders = this.orders[0].relatedOrders && this.orders[0].relatedOrders?.length > 0;

          if (isMultipleOrder && hasRelatedOrders) {
            await this.loadRelatedOrders();
          }
        } else {
          const orderValidation = await this.ordersService.get(this.id, this.selectedAccountId);

          if (orderValidation.model) {
            (this.orders as Order[]).push(orderValidation.model);
          }

          if (this.isPreOrderAndOutsideInitialSellIn) {
            // Include the order number when fetching the product matrix to show products from the order even if they are not in the active season
            this.orderNumber = this.orders[0].orderNumber;
          }
        }
      }

      this.multiplePreOrdersGroupId = (this.orders as DraftModel[]).find((order) => !!order.groupId)?.groupId;

      if (
        !this.multiplePreOrdersGroupId &&
        this.orderType?.toLowerCase() === OrderType.MultiplePreOrder.toLocaleLowerCase()
      ) {
        this.multiplePreOrdersGroupId = uuidv4();
      }
    },

    initMatrixOrders() {
      if (this.editingMatrixOrders.length) {
        this.matrixOrders = this.editingMatrixOrders;
      } else if (this.orders.length) {
        this.matrixOrders = this.orders.map((order, index) => {
          let totalQuantity = 0;

          const matrixProducts = order.products?.map((product: DraftProductModel | OrderProduct) => {
            const matrixProduct = new MatrixProduct();

            matrixProduct.productId = product.productId;
            matrixProduct.productName = product.name;
            matrixProduct.productGroupName = product.productGroupName ?? '';
            matrixProduct.productGroupId = product.productGroupName ?? '';
            matrixProduct.productGroupNumber = product.name;
            matrixProduct.total = product.quantity;

            // ToDo as soon as we start storing the sizeId on the order line we can ditch this.
            matrixProduct.description = product.description;

            totalQuantity += product.quantity;

            return matrixProduct;
          });

          return {
            orderNumber: order.orderNumber,
            orderId: order.id,
            date: toJsDate(toPlainDate(order.dueDate)),
            matrixProducts,
            selected: index === 0,
            total: totalQuantity,
          };
        });
      } else {
        this.matrixOrders = [
          {
            date: toJsDate(toPlainDate(this.startRequestedDate)),
            selected: true,
            matrixProducts: [],
            total: 0,
          },
        ];
      }
    },

    getOrderBreadcrumbMenuItem(orderBreadcrumbItem: AddEditOrderBreadcrumbSteps): BreadcrumbItem {
      return {
        id: orderBreadcrumbItem,
        name: this.$t(`create_edit_order.breadcrumb_menu.${orderBreadcrumbItem}`).toString(),
        routerPath: `/orders/${
          this.id
            ? // order / draft updates
              `${this.id}/edit-products/${this.editType}/`
            : // draft/s creation
              `create/${this.orderType.toLocaleLowerCase()}/${this.startRequestedDate}/${
                this.contactId ? `${this.contactId}/` : ''
              }`
        }${orderBreadcrumbItem}`,
      };
    },

    async proceedToOrderDetails(closeBefore: boolean) {
      if (closeBefore) this.showProductsConfirmationModal = false;
      await this.onUpsertClick();
      if (!closeBefore) this.showProductsConfirmationModal = false;
    },

    executeCurrentStepAction(event: Event) {
      event.stopPropagation();

      switch (this.currentStep) {
        case AddEditOrderBreadcrumbSteps.productsModelsSelection:
          this.$router.replace({
            params: { ...this.$route.params, step: AddEditOrderBreadcrumbSteps.productsQuantities },
          });

          return;
        case AddEditOrderBreadcrumbSteps.productsQuantities:
          this.showProductsConfirmationModal = true;

          return;
        case AddEditOrderBreadcrumbSteps.orderDetails:
          if (this.accountDetails?.onHold) {
            this.updateDraftOnExit();

            return;
          }
          this.submitOrders();

          return;
      }
    },

    mapMatrixProducts(matrixProducts: MatrixProduct[], lines: DraftProductModel[] = []) {
      return matrixProducts.map((matrixProduct) => {
        const line = new DraftProductModel();

        const lineId = lines.find((existingLine) => existingLine.productId === matrixProduct.productId)?.lineId;

        if (lineId) {
          line.lineId = lineId;
        }

        line.productId = matrixProduct.productId;
        line.productGroupName = matrixProduct.productGroupName;
        line.quantity = matrixProduct.total;
        line.name = matrixProduct.productGroupNumber;
        // ToDo we should store the size id on the line, description field should be a computed value
        line.description = matrixProduct.description ?? `${matrixProduct.productName} ${matrixProduct.sizeId}`;

        return line;
      });
    },

    mapExistingOrderMatrixProducts(matrixProducts: MatrixProduct[], lines: OrderProduct[] = []): OrderProduct[] {
      return matrixProducts.map((matrixProduct) => {
        const preExistingProduct = lines.find((existingLine) => existingLine.productId === matrixProduct.productId);

        if (preExistingProduct) {
          return {
            ...preExistingProduct,
            quantityBeforeUpdate: preExistingProduct.quantity,
            quantity: matrixProduct.total,
          };
        }

        const line = new OrderProduct();

        line.productId = matrixProduct.productId;
        line.productGroupId = matrixProduct.productGroupId;
        line.productGroupName = matrixProduct.productGroupName;
        line.quantity = matrixProduct.total;
        line.description = matrixProduct.productGroupName + ' ' + matrixProduct.color + ' ' + matrixProduct.sizeId;
        line.name = matrixProduct.productGroupNumber;

        return line;
      });
    },

    updateAllOrdersAreValid(allOrdersQuantitiesAreValid: boolean) {
      this.allOrdersQuantitiesAreValid = allOrdersQuantitiesAreValid;
    },

    onOrderDateChange(date: Date) {
      this.matrixOrders[0].date = date;

      if (this.isDraft) {
        if (this.orders[0]) {
          this.orders[0].dueDate = this.formatNonLocalisedDateString(this.matrixOrders[0].date as Date);
          this.setEditingOrders(this.orders);
        }

        this.$router.replace({
          params: {
            ...this.$route.params,
            startDate: this.formatDateToIsoStringIgnoringTime(this.matrixOrders[0].date as Date),
          },
        });
      }
    },

    navigateToRoute(route: { path: string }) {
      this.$router.push(route.path);
    },

    addNewOrder(orderMatrix: Partial<MatrixOrder>) {
      if (orderMatrix.selected) {
        this.matrixOrders.forEach((matrixOrder) => (matrixOrder.selected = false));
      }

      this.matrixOrders.push({
        total: 0,
        selected: false,
        matrixProducts: [],
        date: undefined,
        ...orderMatrix,
      });

      if (this.currentStep !== AddEditOrderBreadcrumbSteps.productsQuantities) {
        return this.$router.replace({
          params: { ...this.$route.params, step: AddEditOrderBreadcrumbSteps.productsQuantities },
        });
      }
    },

    editOrderQuantities(orderId: string) {
      this.matrixOrders.forEach((matrixOrder) => (matrixOrder.selected = matrixOrder.orderId === orderId));

      if (this.currentStep !== AddEditOrderBreadcrumbSteps.productsQuantities) {
        return this.$router.replace({
          params: { ...this.$route.params, step: AddEditOrderBreadcrumbSteps.productsQuantities },
        });
      }
    },

    updateOrdersFromViewEditOrders(updatedOrders: Order[]) {
      this.orders = updatedOrders;
      this.matrixOrders.forEach((matrixOrder) => {
        const orderForMatrixOrder = updatedOrders.find((order) => order.id === matrixOrder.orderId);

        if (orderForMatrixOrder) {
          matrixOrder.date = orderForMatrixOrder.dueDate as Date;
        }
      });

      this.setEditingOrders(this.orders);
      this.setEditingMatrixOrders(this.matrixOrders);
    },

    setOrdersAreValidFromViewEditOrders(ordersAreValid: boolean) {
      this.allOrdersViewValid = ordersAreValid;
    },

    async onUpsertClick() {
      this.submitting = true;

      if (!this.editType || this.editType === OrderEditType.Draft) {
        await this.upsertDrafts();
      } else {
        this.updateExistingOrder();
      }

      this.submitting = false;
      this.orderQuantitiesTouched = false;
    },

    async upsertDrafts(upsertOnExit = false) {
      const drafts = cloneDeep(this.orders) as DraftModel[];

      await Promise.all(
        this.matrixOrders.map(async (matrixOrder) => {
          const draft = drafts.find((aDraft) => matrixOrder.orderId && aDraft.id === matrixOrder.orderId);

          if (!draft) {
            const createdDraft = await this.draftsService.createDraftFromMatrixOrder(
              matrixOrder,
              this.contactId || this.userProfile.profile?.id,
              this.orderType,
              this.selectedAccountId,
              this.isSalesRep(),
              // ToDo validate date to avoid casting
              this.formatNonLocalisedDateString(matrixOrder.date as Date),
              this.multiplePreOrdersGroupId,
            );

            if (createdDraft) {
              drafts.push(createdDraft);
            }
          } else {
            await this.updateDraft(draft, matrixOrder);
          }
        }),
      );

      this.orders = drafts;

      this.setEditingMatrixOrders(this.matrixOrders);
      this.setEditingOrders(this.orders);

      if (upsertOnExit) {
        return;
      }

      return this.$router.replace({
        params: { ...this.$route.params, step: AddEditOrderBreadcrumbSteps.orderDetails },
      });
    },

    async updateDraft(draft: DraftModel, matrixOrder: MatrixOrder) {
      draft.dueDate = this.formatNonLocalisedDateString(matrixOrder.date as Date);

      // ToDo matrix should not return products with quantity 0
      const matrixProducts = matrixOrder.matrixProducts.filter((matrixProduct) => matrixProduct.total !== 0);

      draft.products = this.mapMatrixProducts(matrixProducts, draft.products);

      await this.draftsService.update(draft, this.selectedAccountId);

      return draft;
    },

    async updateDraftOnExit() {
      this.canLeavePage = true;
      this.saving = true;

      if (!this.matrixOrders.length || !this.orders.length) {
        return this.$router.push('/orders');
      }

      await Promise.all([await this.upsertDrafts(true), (this.saving = false), this.$router.push('/orders')]);
    },

    async deleteDraftOnExit() {
      this.deleting = true;
      const orders = this.orders as DraftModel[];

      const deletePromises = orders.map(async (draft) => {
        if (draft.id) {
          await this.deleteDraft(draft.id);
        }
      });

      await Promise.all(deletePromises);

      this.canLeavePage = true;
      this.deleting = false;

      return this.$router.push('/orders');
    },

    async deleteDraft(draftId: string) {
      const draftIndexToDelete = this.orders.findIndex((order) => order.id === draftId);

      if (draftIndexToDelete !== -1) {
        await this.draftsService.remove(draftId, this.selectedAccountId);
        this.orders.splice(draftIndexToDelete, 1);
      }

      this.setEditingOrders(this.orders);
    },

    async submitOrders() {
      if (!this.submitting) {
        this.submitting = true;
        let responses: ValidatorResult<any>[];

        if (!this.areExistingOrders) {
          responses = await Promise.all(
            (this.orders as DraftModel[])
              .filter((draft) => draft.orderNumber !== this.orderOverviewId)
              .map((draft) => this.draftsService.submit(draft, this.selectedAccountId)),
          );
        } else {
          const updateOrderObject = this.getUpdateOrderPayload();

          responses = [await this.ordersService.updateOrder(updateOrderObject, this.selectedAccountId)];

          if (responses.every((response) => response.isValid())) {
            responses[0].model = { ...updateOrderObject, type: this.orderType };
          }
        }

        if (responses.every((response) => response.isValid())) {
          this.canLeavePage = true;
          const orderAction = this.getOrderAction();
          this.showSuccessNotification(responses, orderAction);

          this.addCustomGaEvent({
            event: GaCustomEvents.CheckoutStep5,
            checkout_step_name: GaCheckoutStepNames.Confirmation,
          });

          this.addCustomGaEvent({
            event: GaCustomEvents.Purchase,
          });
        }

        this.submitting = false;
      }

      this.setEditingOrders(this.orders);
    },

    getOrderAction(): OrderAction {
      return this.isDraft ? OrderAction.Create : OrderAction.Update;
    },

    updateExistingOrder() {
      const matrixOrder = this.matrixOrders[0];

      if (!this.orders.length) {
        return;
      }

      this.orders[0].products = this.mapExistingOrderMatrixProducts(
        matrixOrder.matrixProducts,
        this.orders[0].products as OrderProduct[],
      );
      this.setEditingMatrixOrders([matrixOrder]);
      this.setEditingOrders(this.orders);

      return this.$router.replace({
        params: { ...this.$route.params, step: AddEditOrderBreadcrumbSteps.orderDetails },
      });
    },

    showSuccessNotification(responses: ValidatorResult<Order>[], orderAction: OrderAction) {
      const confirmedOrderNumber = responses.map((response) => response.model?.orderNumber).join(', ');
      let orderType = responses[0].model?.type;

      if (orderType === OrderType.ReOrder && responses[0].model?.deliveryAddress?.name) {
        orderType = OrderType.DropShip;
      }

      this.$router.replace({
        name: RouteNames.SuccessOrdered,
        params: { orderType, orderNumber: confirmedOrderNumber },
        query: { orderAction },
      });
    },

    getUpdateOrderPayload(): OrderUpdateDto {
      const updatedOrder = this.orders[0] as Order;
      const updateLines = updatedOrder.products
        .filter((line) => line.quantity !== line.quantityBeforeUpdate)
        .map<OrderLineUpdateDto>((line) => ({
          barcode: line.productId,
          quantity: line.quantity,
        }));

      return {
        orderNumber: updatedOrder.orderNumber,
        orderConfirmationId: updatedOrder.orderConfirmation?.id as string,
        contactId: updatedOrder.contactId,
        customerPoNumber: updatedOrder.customerPoNumber,
        lines: updateLines,
      };
    },

    updateMatrixOrders(newOrders: MatrixOrder[]) {
      this.matrixOrders = newOrders;
      this.setEditingMatrixOrders(this.matrixOrders);
    },
  },
}) as any;
</script>
<style lang="scss" scoped>
.add-edit-order {
  width: 100%;
  margin-bottom: 20px;

  .navbar-background {
    @include navbar-background;
    z-index: 5; // this is to keep the calendar on top of the next possible sticky bars
  }

  .add-edit-order-nav-bar {
    @include common-container;
    @include container-background;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 30px 20px;
    border-radius: 0;
    margin: 0px auto;

    .requested-date:deep() {
      width: 160px;
      margin: -8px 0;

      @include breakpoint(small) {
        min-height: 58px;
      }

      @include breakpoint(medium) {
        .vdp-datepicker input {
          width: 100px;
        }
      }

      label {
        text-align: left;
        display: block;
        font-size: 0.75em;
        padding-bottom: 5px;
        cursor: pointer;
        color: var(--primary-text-remark-color);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;

        &:hover {
          overflow: visible;
          white-space: normal;
          display: inline-flex;
        }
      }

      .vdp-datepicker input {
        width: 100px;
      }

      .vdp-datepicker input {
        width: 100px;
      }

      .dp__arrow_top {
        left: 82px;
      }
    }

    .breadcrumb-menu:deep() {
      flex-grow: 1;

      .crumbs {
        justify-content: center;
      }
    }

    .action-buttons {
      min-width: 160px;
      align-self: center;

      .button {
        white-space: nowrap;
      }
    }
  }

  .current-step-content {
    &.pop-up-open {
      position: inherit;
    }
  }

  .current-step-content:deep() {
    .common-container {
      @include common-container;
      padding: 8px 20px;
    }

    .view-edit-orders .view-edit-order-details-container {
      margin-bottom: 20px;
    }
  }

  .spacer {
    padding-left: 2.5px;
    padding-right: 2.5px;
  }
}
</style>
